import { useCallback, useRef, useState } from 'react';
import ImageCropperModal from '@/components/ImageCropperModal';

/**
 * Abre el editor de encuadre y devuelve una promesa con el archivo recortado.
 *
 *   const { cropImage, cropperNode } = useImageCropper();
 *   const cropped = await cropImage(file, { aspect: 1 });   // null si cancela
 *   ...
 *   return <>{cropperNode}...</>;
 *
 * Si el archivo no es una imagen se devuelve tal cual, sin abrir el editor.
 */
export default function useImageCropper() {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    resolve?.(value);
  }, []);

  const cropImage = useCallback((file, options = {}) => {
    if (!file) return Promise.resolve(null);
    if (!file.type || !file.type.startsWith('image/')) return Promise.resolve(file);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({ file, options });
    });
  }, []);

  const cropperNode = request ? (
    <ImageCropperModal
      file={request.file}
      aspect={request.options.aspect ?? 1}
      title={request.options.title}
      allowSkip={request.options.allowSkip !== false}
      maxOutputSize={request.options.maxOutputSize}
      onConfirm={(cropped) => settle(cropped)}
      onSkip={(original) => settle(original)}
      onCancel={() => settle(null)}
    />
  ) : null;

  return { cropImage, cropperNode };
}
